"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Select from "react-select";
import countryList from "react-select-country-list";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Phone, Mail, MapPin } from "lucide-react";
import { SpotlightCard } from "@/components/ui/spotlight-card";
import { useMemo, useState } from "react";
import { CalendlyWidget } from "@/components/ui/calendly-widget";
import Link from "next/link";
function CountrySelect({ name, value, onChange }: { name: string; value: any; onChange: (value: any) => void }) {
  const options = useMemo(() => countryList().getData(), []);

  return (
    <Select
      instanceId="country-select"
      name={name}
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Select your country..."
      styles={{
        control: (base: any) => ({
          ...base,
          borderColor: "#374151",
          backgroundColor: "#1f2937",
          color: "#f9fafb",
          borderRadius: "0.5rem",
          borderWidth: "1px",
          minHeight: "40px",
          "&:hover": {
            borderColor: "#6b7280",
          },
          "&:focus-within": {
            borderColor: "#3b82f6",
            boxShadow: "0 0 0 1px #3b82f6",
          },
        }),
        menu: (base: any) => ({
          ...base,
          maxHeight: 420,
          zIndex: 9999,
          backgroundColor: "#1f2937",
          border: "1px solid #374151",
          borderRadius: "0.5rem",
        }),
        menuList: (base: any) => ({
          ...base,
          maxHeight: 420,
        }),
        option: (base: any, state: any) => ({
          ...base,
          backgroundColor: state.isSelected
            ? "#3b82f6"
            : state.isFocused
              ? "#374151"
              : "transparent",
          color: state.isSelected ? "white" : "#f9fafb",
          padding: "12px 16px",
          "&:hover": {
            backgroundColor: "#374151",
          },
        }),
      }}
    />
  );
}

export default function ContactPage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState<any>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string[]>([]);
  const [showCalendly, setShowCalendly] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError([]);

    // Validate form fields
    const newErrors: string[] = [];
    if (!fullName.trim()) newErrors.push("Full name is required");
    if (!email.trim()) newErrors.push("Email is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.push("Invalid email format");
    if (!phone.trim()) newErrors.push("Phone number is required");
    if (!country) newErrors.push("Country is required");
    if (!message.trim()) newErrors.push("Message is required");

    if (newErrors.length > 0) {
      setError(newErrors);
      return;
    }

    try {
      const formData = {
        name: fullName,
        email,
        phone,
        company,
        message,
        source: 'contact_page'
      };

      console.log('Submitting contact form with data:', formData);

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      console.log('Contact form response status:', res.status);

      if (!res.ok) {
        const errorData = await res.json();
        console.error('Contact form error response:', errorData);
        throw new Error(errorData.error || "Failed to submit form");
      }

      const data = await res.json();
      console.log('Contact form success response:', data);

      // Clear form after successful submission
      setFullName("");
      setEmail("");
      setPhone("");
      setCompany("");
      setCountry(null);
      setMessage("");

      // Show success message
      setError(["Form submitted successfully!"]);
    } catch (error) {
      console.error('Contact form submission error:', error);
      setError([error instanceof Error ? error.message : "Failed to submit form. Please try again."]);
    }
  }
  return (
    <div className="flex flex-col">
      {/* hero Section */}
      <section className="w-full bg-secondary/50 py-20 h-[20rem]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold font-headline text-primary mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground mt-2 max-w-2xl mx-auto">
            Your Vision. Our Intelligence. One Powerful Connection.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <div className="grid lg:grid-cols-5 gap-16 m-16 py-5">
        {/* Contact Form */}
        <div className="lg:col-span-3 border border-primary/50 shadow-[0_0_10px_rgba(0,0,0,0.1)] shadow-primary rounded-lg">
          <SpotlightCard>
            <CardHeader>
              <CardTitle className="font-headline text-2xl">
                Get a Free Quote
              </CardTitle>
              <CardDescription>
                Fill out the form below and one of our AI specialists will get
                back to you shortly.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error.length > 0 && (
                  <div className={`p-4 rounded-lg ${error[0].includes("success") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    <ul className="list-disc list-inside">
                      {error.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input onChange={(e) => setFullName(e.target.value)} value={fullName} id="name" placeholder="John Doe" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      onChange={(e) => setEmail(e.target.value)} value={email}
                      id="email"
                      type="email"
                      placeholder="john.doe@example.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone No.</Label>
                  <Input onChange={(e) => setPhone(e.target.value)} value={phone} id="phone" placeholder="+xxxxxxx" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <Input onChange={(e) => setCompany(e.target.value)} value={company} id="company" placeholder="Your Company Inc." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <CountrySelect
                    name="country"
                    value={country}
                    onChange={(option) => setCountry(option)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">How can we help you?</Label>
                  <Textarea
                    onChange={(e) => setMessage(e.target.value)} value={message}
                    id="message"
                    placeholder="Tell us about your project or business needs..."
                    rows={6}
                  />
                </div>
                <Button type="submit" size="lg" className="w-full sm:w-auto">
                  Send Message
                </Button>
              </form>
            </CardContent>
          </SpotlightCard>
        </div>

        {/* Contact Info & Demo */}
        <div className="lg:col-span-2 space-y-8 py-5 px-10">
          <div className="flex flex-col gap-4">
            <h2 className="text-4xl font-bold py-2">Contact Information</h2>
            <p className="text-muted-foreground">
              We are here to answer any questions you may have about our AI services,
              BPO solutions, or technical implementations. Reach out to us and we'll
              respond as soon as we can.
            </p>
            <div className="flex flex-col gap-8">
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-full p-4">
                  <MapPin className="w-10 h-10 text-secondary font-bold text-3xl text-center" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-lg font-bold text-primary"> Address :</p>
                  <span className="text-md text-muted-foreground">
                    Madhya Palash, Rai Villa Rd, Devidanga, Siliguri, West Bengal 734003
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-full p-4">
                  <Phone className="w-10 h-10 text-secondary font-bold text-3xl text-center" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-lg font-bold text-primary"> Phone :</p>
                  <span className="text-md text-muted-foreground">
                    +91 9800096281
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-full p-4">
                  <Mail className="w-10 h-10 text-secondary font-bold text-3xl text-center" />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-lg font-bold text-primary"> Email :</p>
                  <span className="text-md text-muted-foreground">
                    <Link href="mailto:info@tellistechnologies.com">
                      info@tellistechnologies.com
                    </Link>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="flex flex-col gap-4 mt-8">
            <h3 className="text-2xl font-bold py-2">Business Hours</h3>
            <div className="flex flex-col gap-2 text-muted-foreground">
              <p><span className="font-semibold">Monday - Friday:</span> 9:00 AM - 6:00 PM IST</p>
              <p><span className="font-semibold">Saturday:</span> 10:00 AM - 2:00 PM IST</p>
              <p><span className="font-semibold">Sunday:</span> Closed</p>
            </div>
          </div>

          {/* Why Contact Us */}
          <div className="flex flex-col gap-4 mt-8">
            <h3 className="text-2xl font-bold py-2">Why Work With Tellis Technologies?</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>Industry-leading AI voice agents and chatbot solutions</li>
              <li>Experienced BPO team with multilingual capabilities</li>
              <li>Custom workflow automation for business efficiency</li>
              <li>RAG systems for intelligent data management</li>
              <li>24/7 technical support and dedicated account management</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Google Map Section */}
      <div className="w-full h-[40rem] p-16 bg-secondary/50">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3562.2033435615012!2d88.42760387565744!3d26.769787376732616!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39e4417e50a3b187%3A0x1b0f47ea3a186f47!2sTellis%20Technologies%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1752733755249!5m2!1sen!2sin"
          className="w-full h-full"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  );
}
